/**
 * useDraftAutosave — Hook de persistance serveur des funnels (Lead Draft v1).
 *
 * Sauvegarde debounced (800ms) à chaque changement de payload ou d'étape.
 * Token stocké dans sessionStorage pour reprise cross-onglet.
 * sendBeacon à l'abandon de page (beforeunload).
 *
 * Usage :
 *   const { draftToken, saveDraft } = useDraftAutosave('diagnostic', { siret, answers });
 */

import { useCallback, useEffect, useRef } from 'react';
import type { FunnelId } from '../lib/tracking';

const DRAFT_TOKEN_KEY = (funnelId: string) => `sef_draft_token_${funnelId}`;

interface DraftAutosaveOptions {
  step: string;
  stepIndex: number;
  payload: Record<string, unknown>;
  stepsCompleted?: string[];
  audience?: string;
  segment?: string;
  intent?: string;
  source?: string;
  /** Si true, ne créé pas de draft (ex: funnel déjà soumis) */
  disabled?: boolean;
}

interface DraftAutosaveResult {
  draftToken: string | null;
  saveDraft: () => void;
}

export function useDraftAutosave(
  funnelId: FunnelId,
  options: DraftAutosaveOptions,
): DraftAutosaveResult {
  const tokenRef = useRef<string | null>(
    typeof window !== 'undefined'
      ? sessionStorage.getItem(DRAFT_TOKEN_KEY(funnelId))
      : null,
  );
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<DraftAutosaveOptions>(options);

  // Mettre à jour la référence sans re-render
  useEffect(() => {
    pendingRef.current = options;
  });

  const saveDraft = useCallback(async () => {
    if (options.disabled || typeof window === 'undefined') return;

    const opts = pendingRef.current;
    const sessionId = sessionStorage.getItem('sef_session_id') ?? undefined;

    try {
      if (!tokenRef.current) {
        // Premier appel : créer le draft
        const res = await fetch('/api/drafts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            funnelId,
            sessionId,
            audience: opts.audience,
            segment: opts.segment,
            intent: opts.intent,
            source: opts.source,
            stepCurrent: opts.step,
            payloadPartial: opts.payload,
          }),
        });
        if (res.ok) {
          const data = (await res.json()) as { draftToken: string };
          tokenRef.current = data.draftToken;
          sessionStorage.setItem(DRAFT_TOKEN_KEY(funnelId), data.draftToken);
        }
      } else {
        // Mise à jour de progression
        await fetch(`/api/drafts/${tokenRef.current}/progress`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stepCurrent: opts.step,
            stepsCompleted: opts.stepsCompleted,
            payloadPartial: opts.payload,
          }),
        });
      }
    } catch {
      // Fail silently — le draft est un bonus, pas bloquant
    }
  }, [funnelId, options.disabled]);

  // Autosave debounced 800ms
  useEffect(() => {
    if (options.disabled) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void saveDraft(), 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // On veut re-déclencher quand step ou payload change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.step, options.stepIndex, JSON.stringify(options.stepsCompleted)]);

  // sendBeacon à l'abandon de page
  useEffect(() => {
    const onUnload = () => {
      const token = tokenRef.current;
      if (!token || options.disabled) return;
      const url = `/api/drafts/${token}/progress`;
      const body = JSON.stringify({
        stepCurrent: pendingRef.current.step,
        stepsCompleted: pendingRef.current.stepsCompleted,
        payloadPartial: pendingRef.current.payload,
      });
      // sendBeacon fire-and-forget — pas de réponse attendue
      navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
    };
    window.addEventListener('pagehide', onUnload);
    return () => window.removeEventListener('pagehide', onUnload);
  }, [options.disabled]);

  return { draftToken: tokenRef.current, saveDraft };
}

/** Marque le draft comme soumis après la création du lead. */
export async function submitDraft(funnelId: FunnelId, leadId: string): Promise<void> {
  if (typeof window === 'undefined') return;
  const token = sessionStorage.getItem(DRAFT_TOKEN_KEY(funnelId));
  if (!token) return;
  try {
    await fetch(`/api/drafts/${token}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ leadId }),
    });
    sessionStorage.removeItem(DRAFT_TOKEN_KEY(funnelId));
  } catch {
    // Fail silently
  }
}
